import React, { useEffect, useRef, useState } from 'react';

import { apiSahClaimItems } from '~constants/api';
import {
  SA_ATTACHMENT_EXTENSIONS,
  SA_ATTACHMENT_MAX_SIZE_MB,
  SA_ATTACHMENT_MIME_TYPES,
  SA_ATTACHMENT_TYPE_OPTIONS,
} from '~support-at-home/constants/attachments';

import { getErrorMessage } from '~libs/axiosWrapper';
import { downloadFile } from '~libs/genericAction';
import { useToast } from '~libs/toast';
import { parseSahApiError } from '~support-at-home/lib/sahApiError';

import { Button, ClickableIcon, ConfirmDialog, ErrorResponse, FileUpload, Select, Spinner } from '~components';
import { FilePreview } from '~components/file-area/FilePreview';
import Icon, { faChevronUp, faEye, faTrash, faTriangleExclamation } from '~components/icon';

import { ClaimItemAttachment } from '~support-at-home/models';

import {
  useDeleteClaimItemAttachmentMutation,
  useGetClaimItemAttachmentsQuery,
  useUploadClaimItemAttachmentMutation,
} from '~support-at-home/actions/claims';

interface ComponentProps {
  itemId: string;
  /**
   * Claim the item belongs to - see the equivalent note on ClaimItemRequirements. Carried through
   * purely so the upload/delete mutations can invalidate claim-level tags (the `sa_status` cell and
   * the claim banner) rather than only the item itself.
   */
  claimId?: string;
  /**
   * Whether the parent ClaimItemRequirements modal is open. The modal (owned by the parent) already
   * unmounts this panel entirely on close, but the fade-out transition leaves it mounted for a beat
   * with this prop already false - `skip`ping the query and resetting the upload form on that edge
   * mirrors the old trigger+modal's own `handleClose`, which used to own `isOpen` itself.
   */
  isOpen: boolean;
}

interface Preview {
  id: string;
  url: string;
  fileName: string;
  fileType: string;
}

const TYPE_LABELS: Record<string, string> = Object.fromEntries(
  SA_ATTACHMENT_TYPE_OPTIONS.map((option) => [option.id, option.name]),
);
const TYPE_SELECT_OPTIONS = SA_ATTACHMENT_TYPE_OPTIONS.map((option) => ({ value: option.id, label: option.name }));

/**
 * ClaimItemAttachmentsPanel
 *
 * Attachments tab content for ClaimItemRequirements (TAV-2592) - the upload/list/preview/delete
 * body and all of its state and RTK Query hooks, extracted from the old ClaimItemAttachments
 * trigger+modal (TAV-2591). This component owns no modal or trigger of its own; the parent renders
 * it as one `Tab.content` inside a shared `TabContainer`.
 */
const ClaimItemAttachmentsPanel: React.FC<ComponentProps> = ({ itemId, claimId, isOpen }) => {
  const [attachmentType, setAttachmentType] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // FileUpload keeps its own internal fileName/error state with no reset prop - remounting it via a
  // changing `key` is how the picker is cleared after a successful upload.
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<ClaimItemAttachment | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  // Monotonic token identifying the newest view request. Bumped by every handleView AND by the
  // close-effect below, so a download that resolves after the modal shut - or after a second row was
  // clicked - can tell it is no longer wanted, revoke the URL it just created and leave state alone.
  // Without it the resolved handler re-populates `preview` on a closed modal, and the next open
  // renders that row already expanded over a stale blob.
  const viewRequestRef = useRef(0);

  const { showToast } = useToast();

  const {
    data: attachments,
    isFetching: isListLoading,
    error: listError,
  } = useGetClaimItemAttachmentsQuery({ id: itemId }, { skip: !isOpen });

  const [uploadAttachment, { isLoading: isUploading }] = useUploadClaimItemAttachmentMutation();
  const [deleteAttachment, { isLoading: isDeleting }] = useDeleteClaimItemAttachmentMutation();

  // Revoke the blob object URL whenever the previewed file changes or the component unmounts -
  // otherwise every "view" click leaks a URL for the lifetime of the tab.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  // Mirrors the old trigger+modal's handleClose: the modal now belongs to ClaimItemRequirements, so
  // this panel resets its own form/preview state whenever it observes the modal close rather than
  // owning the close itself.
  useEffect(() => {
    if (!isOpen) {
      viewRequestRef.current += 1;
      setAttachmentType(null);
      setSelectedFile(null);
      setFileUploadKey((key) => key + 1);
      setPreview(null);
      setViewError(null);
      setViewingId(null);
    }
  }, [isOpen]);

  const resetUploadForm = () => {
    setAttachmentType(null);
    setSelectedFile(null);
    setFileUploadKey((key) => key + 1);
  };

  const handleUpload = async () => {
    if (!selectedFile || !attachmentType) return;

    try {
      const result = await uploadAttachment({
        id: itemId,
        claimId,
        file: selectedFile,
        attachment_type: attachmentType,
      }).unwrap();
      resetUploadForm();
      showToast('success', 'Attachment uploaded to Services Australia');
      // `warnings` carries real "file is at SA but unconfirmed" states - never silently dropped.
      result.warnings.forEach((warning) => showToast('info', warning));
    } catch (error) {
      showToast('danger', parseSahApiError(getErrorMessage(error)));
    }
  };

  // Accordion toggle: clicking the open row's control collapses it. Only ever one preview is held
  // in state, so opening a second row implicitly closes the first - the single-open rule is a
  // property of the data shape, not a rule enforced on top of it.
  const handleView = async (attachment: ClaimItemAttachment) => {
    setViewError(null);

    if (preview?.id === attachment.id) {
      viewRequestRef.current += 1;
      // No revoke here either - see the note in the resolved branch below. The [preview] effect runs
      // its cleanup against the OLD value on this transition too, so revoking manually was a
      // second, redundant call. One owner for blob cleanup, one exception (the superseded branch,
      // whose url no state ever adopts).
      setPreview(null);
      return;
    }

    viewRequestRef.current += 1;
    const requestId = viewRequestRef.current;

    setViewingId(attachment.id);
    try {
      const url = await downloadFile(`${apiSahClaimItems}/attachments/${attachment.id}`, attachment.file_name);
      // Superseded while in flight (modal closed, or another row clicked). The blob was still
      // created, so it has to be revoked here - returning early without this leaks it for the
      // lifetime of the tab, since no state change will ever reference it.
      if (viewRequestRef.current !== requestId) {
        URL.revokeObjectURL(url);
        return;
      }
      // Deliberately does NOT revoke the outgoing url here. The effect above is keyed on [preview],
      // so React already runs its cleanup against the OLD value on every switch - revoking in both
      // places was a double-revoke (a spec-defined no-op, but it read as accidental rather than
      // belt-and-braces). Leaving it to the effect also keeps this updater pure: React may invoke a
      // state updater more than once, and a side effect inside one is not safe to repeat.
      setPreview({ id: attachment.id, url, fileName: attachment.file_name, fileType: attachment.file_type });
    } catch (error) {
      if (viewRequestRef.current !== requestId) return;
      setViewError(error instanceof Error ? error.message : 'Failed to load attachment');
    } finally {
      // Guarded too: an unguarded reset would clear the spinner belonging to a NEWER request.
      if (viewRequestRef.current === requestId) setViewingId(null);
    }
  };

  const handleDeleteConfirm = async (confirmed: boolean) => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!confirmed || !target) return;

    try {
      const result = await deleteAttachment({ attachmentId: target.id, claimItemId: itemId, claimId }).unwrap();
      // Reads LIVE state, not the `preview` this handler closed over. `setDeleteTarget(null)` above
      // dismisses the dialog before the await, and only the DELETE icons are disabled while the
      // mutation is in flight - the view icons stay live - so the user can open a different row's
      // preview while this delete is still running. The old `if (preview?.id === target.id)` then
      // tested a stale closure value and called an unconditional `setPreview(null)`, collapsing the
      // preview the user had just opened. The updater form asks the question of whatever is
      // currently showing, and is pure, so React is free to invoke it more than once.
      setPreview((current) => (current?.id === target.id ? null : current));
      showToast('success', 'Attachment removed');
      result.warnings.forEach((warning) => showToast('info', warning));
    } catch (error) {
      showToast('danger', parseSahApiError(getErrorMessage(error)));
    }
  };

  return (
    <>
      <div className="d-flex flex-column gap-3">
        <div>
          <label htmlFor={`claim-item-attachments-type-${itemId}`} className="form-label">
            Attachment type
          </label>
          {/* mb-3 separates the type picker from the drop zone, which otherwise butts straight
              against the select's lower edge. */}
          <div className="mb-3">
            <Select
              id={`claim-item-attachments-type-${itemId}`}
              name="attachment_type"
              value={attachmentType}
              options={TYPE_SELECT_OPTIONS}
              onChange={(_, value) => setAttachmentType((value as string) ?? null)}
              placeholder="Select a type"
              ariaLabel="Attachment type"
            />
          </div>
          <FileUpload
            key={`file-upload-${fileUploadKey}`}
            allowedExtensions={SA_ATTACHMENT_EXTENSIONS}
            allowedMimeTypes={SA_ATTACHMENT_MIME_TYPES}
            maxSizeMB={SA_ATTACHMENT_MAX_SIZE_MB}
            onChange={setSelectedFile}
            // FileUpload only calls onChange for files that PASS its validation, so without this a
            // rejected second pick left the first file armed: the picker showed "File size exceeds 5
            // MB" while the enabled button still uploaded the earlier PDF - the wrong evidence,
            // against a real claim item, on a live Services Australia call. Dropping the selection
            // makes the disabled button agree with the visible error.
            onError={(error) => {
              if (error) setSelectedFile(null);
            }}
            label="Upload an attachment"
            description={`PDF, JPG, PNG or TIFF, up to ${SA_ATTACHMENT_MAX_SIZE_MB}MB`}
          />
          <div className="d-flex justify-content-end mt-2">
            <Button
              color="success"
              disabled={!selectedFile || !attachmentType || isUploading}
              loading={isUploading}
              // Button's `loading` prop replaces children with a spinner, which loses the button's
              // accessible name - an explicit aria-label keeps `getByRole`/screen readers working.
              aria-label="Upload attachment to Services Australia"
              onClick={handleUpload}
              dataCy={`claim-item-attachments-upload-button-${itemId}`}
            >
              Upload attachment
            </Button>
          </div>
        </div>

        <ErrorResponse message={listError ? getErrorMessage(listError) : null} />
        <ErrorResponse message={viewError} />

        <div>
          <h6 className="fw-semibold">Uploaded attachments</h6>
          {isListLoading && <Spinner loading size={20} />}
          {!isListLoading && (attachments ?? []).length === 0 && (
            <div className="text-muted">No attachments have been uploaded for this item yet.</div>
          )}
          {(attachments ?? []).map((attachment) => {
            const isViewOpen = preview?.id === attachment.id;
            return (
              <div key={attachment.id} className="border-bottom">
                <div
                  className="d-flex align-items-center gap-2 py-1"
                  data-cy={`claim-item-attachments-row-${attachment.id}`}
                >
                  <span className="flex-grow-1">{attachment.file_name}</span>
                  <span className="text-muted">{TYPE_LABELS[attachment.attachment_type ?? ''] ?? 'Other'}</span>
                  {!attachment.sa_attachment_id && (
                    <Icon
                      icon={faTriangleExclamation}
                      color="orange"
                      title="Uploaded to Services Australia, but the attachment reference could not be confirmed"
                    />
                  )}
                  {/* Eye = collapsed, chevron-up = expanded, matching the accordion convention: the
                      control shows the action, and while open that action is "collapse". */}
                  <ClickableIcon
                    id={`claim-item-attachments-view-${attachment.id}`}
                    icon={isViewOpen ? faChevronUp : faEye}
                    toolTip={isViewOpen ? 'Hide attachment' : 'View attachment'}
                    ariaLabel={`${isViewOpen ? 'Hide' : 'View'} attachment ${attachment.file_name}`}
                    ariaExpanded={isViewOpen}
                    dataCy={`claim-item-attachments-view-button-${attachment.id}`}
                    disabled={viewingId === attachment.id}
                    onClick={() => handleView(attachment)}
                  />
                  <ClickableIcon
                    id={`claim-item-attachments-delete-${attachment.id}`}
                    icon={faTrash}
                    color="danger"
                    toolTip="Delete attachment"
                    ariaLabel={`Delete attachment ${attachment.file_name}`}
                    dataCy={`claim-item-attachments-delete-button-${attachment.id}`}
                    disabled={isDeleting}
                    onClick={() => setDeleteTarget(attachment)}
                  />
                </div>
                {isViewOpen && preview && (
                  <div className="pb-2" data-cy={`claim-item-attachments-preview-${attachment.id}`}>
                    <FilePreview fileData={preview.url} fileType={preview.fileType} fileName={preview.fileName} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        messages={[`Are you sure you want to delete "${deleteTarget?.file_name}"? This cannot be undone.`]}
        useList={false}
        onClose={handleDeleteConfirm}
      />
    </>
  );
};

export default ClaimItemAttachmentsPanel;
